import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

import { ChevronDownIcon } from '@shared/ui/icons';

import { cn } from '../cn';

/**
 * A single-choice control that behaves the same way in every browser.
 *
 * **The native `<select>` is what this replaces, and the reason is that its
 * popup is not ours.** The operator sees a list the user agent draws, placed
 * where the user agent decides: on this console it opened *upward* over the
 * field it belongs to, and the built-in arrow kept pointing down the whole time
 * the list was open. Neither is addressable from a stylesheet — the popup is not
 * in the document and the arrow is not a node — so an authoring form that wants
 * a list which opens beneath its field and an indicator that says whether the
 * list is open has to draw both.
 *
 * **It opens downward and flips only when it would not fit.** The flip measures
 * the rendered panel rather than guessing at a constant: a two-option list and a
 * twenty-option list need different room, and one number standing in for both
 * puts the short list far above the field it acts on.
 *
 * **Focus stays on the trigger and `aria-activedescendant` moves.** Moving real
 * focus into the list is the other legitimate pattern, but it takes the
 * `aria-describedby` hint and the field's error message off the focused element
 * — and on an authoring form both are the only statement of why a value was
 * refused.
 *
 * The chevron turns through the **block** axis, so it is not mirrored under
 * `rtl`: up and down do not reverse with reading direction.
 */

export interface SelectOption {
  value: string;
  label: string;
}

/** One step of the design system's tightest gap, in the one place a token cannot reach. */
const VIEWPORT_GUTTER = 8;

export function Select({
  id,
  value,
  options,
  placeholder,
  onChange,
  onBlur,
  describedBy,
  invalid = false,
  disabled = false,
}: {
  id: string;
  value: string;
  options: readonly SelectOption[];
  /**
   * The empty choice, drawn as the first row.
   *
   * Omitting it removes the row: a field whose value cannot be cleared should
   * not offer a clearing option the schema will then refuse.
   */
  placeholder?: string | undefined;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  describedBy?: string | undefined;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const listId = useId();
  const optionIdPrefix = useId();

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<'below' | 'above'>('below');
  const [activeIndex, setActiveIndex] = useState(-1);

  const rows: SelectOption[] =
    placeholder === undefined ? [...options] : [{ value: '', label: placeholder }, ...options];

  const selectedIndex = rows.findIndex((row) => row.value === value);
  const selected = selectedIndex === -1 ? undefined : rows[selectedIndex];

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  function openList(startAt: number) {
    if (disabled) return;
    setActiveIndex(startAt);
    setOpen(true);
  }

  function commit(index: number) {
    const row = rows[index];
    if (!row) return;
    onChange(row.value);
    close();
    triggerRef.current?.focus();
  }

  /**
   * Place the panel — **scroll first, flip only as a last resort**.
   *
   * The panel is in the document by the time this runs, so its real height is
   * readable here, which is what makes both decisions measurements rather than
   * estimates.
   *
   * A list pressed near the foot of a long form has no room beneath it, and
   * flipping is the wrong first answer: it puts the list over the field the
   * operator just pressed, which is precisely the native `<select>` behaviour
   * this control exists to replace. Bringing the field up its own page costs
   * nothing and leaves the list where the press implied it would be. The flip
   * survives for the case scrolling cannot fix — a list taller than the room a
   * centred field leaves — because a panel hanging off the viewport foot is
   * worse than one above the trigger.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const list = listRef.current;
    if (!trigger || !list) return;

    const needed = list.offsetHeight + VIEWPORT_GUTTER;
    if (window.innerHeight - trigger.getBoundingClientRect().bottom < needed) {
      rootRef.current?.scrollIntoView({ block: 'center' });
    }

    // Read again: the scroll above has already happened, so this is where the
    // trigger actually is rather than where it was when the press landed.
    const handle = trigger.getBoundingClientRect();
    const roomBelow = window.innerHeight - handle.bottom;
    setPlacement(roomBelow >= needed || handle.top < needed ? 'below' : 'above');
  }, [open, rows.length]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  /**
   * A press outside closes it. **A scroll does not**, and that is deliberate.
   *
   * The panel is absolutely positioned against the trigger's own containing
   * block, so it travels with the field and never ends up pointing at a
   * different one — the reason a `fixed` menu has to close on scroll does not
   * apply here. Closing anyway would cancel the list the moment the placement
   * effect above scrolled the field into view.
   */
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, close]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;

    if (!open) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        openList(selectedIndex === -1 ? 0 : selectedIndex);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((current) => Math.min(rows.length - 1, current + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => Math.max(0, current - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(rows.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-describedby={describedBy}
        aria-invalid={invalid}
        aria-activedescendant={open && activeIndex >= 0 ? `${optionIdPrefix}-${activeIndex}` : undefined}
        onClick={() => (open ? close() : openList(selectedIndex === -1 ? 0 : selectedIndex))}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className={cn(
          'hit-target flex w-full items-center justify-between gap-2 rounded-sm border border-border-strong bg-surface px-3 text-start text-sm',
          // **The placeholder is text, not a decoration.** `--ink-faint` is
          // 3.71:1 on `--surface` in the light theme and 3.29:1 in the dark
          // one — under 4.5:1 in both, which `axe` reported at *serious* on
          // «اختر تصنيفاً…» and «اختر تصنيفاً عمريّاً…» on every authoring
          // route. `--ink-muted` clears it at 7.33:1 and 6.98:1 and still
          // reads as the quieter of the two states beside the chosen value's
          // full `--ink`.
          selected && selected.value !== '' ? 'text-ink' : 'text-ink-muted',
          disabled && 'cursor-not-allowed opacity-45',
        )}
      >
        <bdi className="truncate">{selected?.label ?? placeholder ?? ''}</bdi>
        <ChevronDownIcon
          className={cn('shrink-0 text-ink-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className={cn(
            'absolute z-30 m-0 max-h-[16rem] list-none overflow-y-auto rounded-sm border border-border bg-surface-raised p-1 [inset-inline:0] [overscroll-behavior-block:contain]',
            placement === 'below' ? 'top-full mt-1' : 'bottom-full mb-1',
          )}
        >
          {rows.map((row, index) => {
            const chosen = row.value === value;

            return (
              <li key={row.value === '' ? 'select-empty-choice' : row.value}>
                <button
                  id={`${optionIdPrefix}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={chosen}
                  data-active={index === activeIndex}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(index)}
                  className={cn(
                    'flex w-full items-center rounded-sm px-3 py-2 text-start text-sm',
                    chosen ? 'bg-accent-soft text-accent-text' : 'text-ink',
                    index === activeIndex && !chosen && 'bg-hover-bg',
                    // The «no choice» row, held to the same floor as the
                    // trigger it fills — it is the same sentence in the list.
                    row.value === '' && !chosen && 'text-ink-muted',
                  )}
                >
                  <bdi className="truncate">{row.label}</bdi>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
